import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Cropper, CropperRef, CircleStencil } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import { Camera, Save, Stethoscope, User, Phone, Shield, ImageIcon, Upload, Trash2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { useAuthStore, ROL } from '@/store/auth.store';
import { EspecialidadesManager } from '@/components/personal/EspecialidadesManager';
import { LogoCropModal } from '@/components/ui/LogoCropModal';
import { getErrorMessage } from '@/lib/errors';

// ── Estilos reutilizables ─────────────────────────────────────────────────────

const inputCls =
  'w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white disabled:bg-gray-50 disabled:text-gray-400';
const selectCls =
  'w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';
const labelCls = 'block text-xs font-medium text-gray-500 mb-1';

// ── Constantes de rol ─────────────────────────────────────────────────────────

const ROL_LABEL: Record<string, string> = {
  rolsadm01x: 'Super Administrador',
  roladmcl02: 'Administrador',
  rolmedc03y: 'Médico',
  rolasst04z: 'Asistente',
  rolrecp05w: 'Recepcionista',
  rolmedadm06: 'Médico Administrador',
};

const ROL_COLOR: Record<string, string> = {
  rolsadm01x: 'bg-red-50 text-red-700 border-red-100',
  roladmcl02: 'bg-orange-50 text-orange-700 border-orange-100',
  rolmedc03y: 'bg-blue-50 text-blue-700 border-blue-100',
  rolasst04z: 'bg-green-50 text-green-700 border-green-100',
  rolrecp05w: 'bg-purple-50 text-purple-700 border-purple-100',
  rolmedadm06: 'bg-cyan-50 text-cyan-700 border-cyan-100',
};

const SEXO_OPTS = [
  { value: '', label: 'Sin especificar' },
  { value: 'masculino', label: 'Masculino' },
  { value: 'femenino', label: 'Femenino' },
  { value: 'otro', label: 'Otro' },
];

function getInitials(name: string) {
  return (name || '').split(' ').map(w => w[0] ?? '').join('').toUpperCase().slice(0, 2);
}

// ── Modal de recorte de foto ──────────────────────────────────────────────────

interface CropModalProps {
  src: string;
  onSave: (blob: Blob) => void;
  onCancel: () => void;
}

function CropModal({ src, onSave, onCancel }: CropModalProps) {
  const cropperRef = useRef<CropperRef>(null);

  function handleSave() {
    const canvas = cropperRef.current?.getCanvas({ width: 256, height: 256 });
    canvas?.toBlob(b => b && onSave(b), 'image/jpeg', 0.9);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-800">Ajustar foto</p>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>
        <div style={{ height: 300 }}>
          <Cropper
            ref={cropperRef}
            src={src}
            stencilComponent={CircleStencil}
            stencilProps={{ aspectRatio: 1 }}
            style={{ height: 300 }}
          />
        </div>
        <div className="px-5 pb-5 pt-4 flex justify-end gap-2">
          <button onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={handleSave}
            className="px-4 py-2 text-sm rounded-lg text-white font-medium"
            style={{ backgroundColor: '#0e85bf' }}>
            Guardar foto
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sección de formulario ─────────────────────────────────────────────────────

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-50">
        <div className="w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600">
          {icon}
        </div>
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────

export function MiPerfilPage() {
  const qc = useQueryClient();
  const { usuario, updateUsuario } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => api.get('/api/auth/me').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const [form, setForm] = useState({
    nombre: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    sexo: '',
    telefono: '',
  });

  useEffect(() => {
    if (!profile) return;
    setForm({
      nombre:          profile.nombre          ?? '',
      apellidoPaterno: profile.apellidoPaterno ?? '',
      apellidoMaterno: profile.apellidoMaterno ?? '',
      sexo:            profile.sexo            ?? '',
      telefono:        profile.telefono        ?? '',
    });
  }, [profile]);

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [field]: e.target.value }));

  const esMedicoRol = [ROL.MEDICO, ROL.MEDICO_ADMIN].includes(profile?.rolId);

  // ── Logo propio del médico ─────────────────────────────────────────────────

  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoCropSrc,   setLogoCropSrc]   = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const { data: medicoData, refetch: refetchMedico } = useQuery({
    queryKey: ['medico-me'],
    queryFn: () => api.get('/api/medicos/me').then(r => r.data),
    enabled: esMedicoRol,
  });

  const clinicaLogoUrl: string | null = medicoData?.clinica?.logoUrl ?? null;
  const logoPropio: string | null     = medicoData?.logoPropio ?? null;
  const usandoPropio = !!logoPropio;

  function handleLogoFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setLogoCropSrc(ev.target?.result as string);
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  async function handleLogoCropSave(blob: Blob) {
    setLogoCropSrc(null);
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'logo-medico.png');
      const { data: uploadData } = await api.post(
        `/api/storage/upload?entityId=${profile.id}&existingUrl=${encodeURIComponent(logoPropio ?? '')}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      await api.patch('/api/medicos/me', { logoPropio: uploadData.url });
      refetchMedico();
      toast.success('Logo propio actualizado');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al subir el logo'));
    } finally {
      setUploadingLogo(false);
    }
  }

  async function handleLogoDelete() {
    try {
      await api.patch('/api/medicos/me', { logoPropio: null });
      refetchMedico();
      toast.success('Se usará el logo de la clínica');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al restablecer el logo'));
    }
  }

  // ── Guardar datos ──────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: (data: typeof form) => api.patch('/api/auth/me', data).then(r => r.data),
    onSuccess: (updated) => {
      qc.setQueryData(['auth-me'], (old: any) => ({ ...old, ...updated }));
      updateUsuario({ nombre: updated.nombre, fotoUrl: updated.fotoUrl });
      toast.success('Perfil actualizado');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al guardar los cambios')),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    saveMutation.mutate(form);
  }

  // ── Upload de foto ─────────────────────────────────────────────────────────

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setCropSrc(ev.target?.result as string);
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  async function handleCropSave(blob: Blob) {
    setCropSrc(null);
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'foto.jpg');
      const { data } = await api.post(
        `/api/storage/upload?entityId=${profile.id}&existingUrl=${encodeURIComponent(profile.fotoUrl ?? '')}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      await api.patch('/api/auth/me', { fotoUrl: data.url });
      qc.setQueryData(['auth-me'], (old: any) => ({ ...old, fotoUrl: data.url }));
      updateUsuario({ fotoUrl: data.url });
      toast.success('Foto actualizada');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al subir la foto'));
    } finally {
      setUploadingPhoto(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const fotoUrl = profile?.fotoUrl;
  const fotoSrc = fotoUrl
    ? `${fotoUrl}?v=${profile?.updatedAt ? new Date(profile.updatedAt).getTime() : 0}`
    : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 rounded-full border-2 border-primary-300 border-t-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <>
      {cropSrc && (
        <CropModal
          src={cropSrc}
          onSave={handleCropSave}
          onCancel={() => setCropSrc(null)}
        />
      )}

      {logoCropSrc && (
        <LogoCropModal
          src={logoCropSrc}
          title="Recortar logo propio"
          onSave={handleLogoCropSave}
          onCancel={() => setLogoCropSrc(null)}
        />
      )}

      <div className="max-w-2xl mx-auto px-6 py-8">
        <h1 className="text-xl font-bold text-gray-900 mb-6">Mi perfil</h1>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* ── Avatar ─────────────────────────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-6 py-5">
            <div className="flex items-center gap-5">
              {/* Foto */}
              <div className="relative flex-shrink-0">
                <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-md flex items-center justify-center text-white text-2xl font-bold"
                  style={{ backgroundColor: '#0e85bf' }}>
                  {getInitials(profile?.nombre ?? '')}
                  {fotoSrc && (
                    <img src={fotoSrc} alt="foto" className="absolute inset-0 w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                  )}
                </div>
                <button
                  type="button"
                  disabled={uploadingPhoto}
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center text-gray-500 hover:text-primary-600 hover:border-primary-300 transition-colors disabled:opacity-50"
                  title="Cambiar foto"
                >
                  {uploadingPhoto
                    ? <span className="w-3 h-3 border border-primary-500 border-t-transparent rounded-full animate-spin" />
                    : <Camera size={13} />
                  }
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-lg font-bold text-gray-900 truncate">
                  {[profile?.nombre, profile?.apellidoPaterno, profile?.apellidoMaterno].filter(Boolean).join(' ') || '—'}
                </p>
                <p className="text-sm text-gray-400 truncate">{profile?.email}</p>
                <span className={`mt-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${ROL_COLOR[profile?.rolId] ?? 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                  {ROL_LABEL[profile?.rolId] ?? profile?.rolId}
                </span>
              </div>
            </div>
          </div>

          {/* ── Datos personales ────────────────────────────────────────────── */}
          <Section icon={<User size={14} />} title="Datos personales">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-3">
                <label className={labelCls}>Nombre(s)</label>
                <input className={inputCls} value={form.nombre} onChange={set('nombre')} placeholder="Nombre(s)" required />
              </div>
              <div>
                <label className={labelCls}>Apellido paterno</label>
                <input className={inputCls} value={form.apellidoPaterno} onChange={set('apellidoPaterno')} placeholder="Apellido paterno" />
              </div>
              <div>
                <label className={labelCls}>Apellido materno</label>
                <input className={inputCls} value={form.apellidoMaterno} onChange={set('apellidoMaterno')} placeholder="Apellido materno" />
              </div>
              <div>
                <label className={labelCls}>Sexo</label>
                <select className={selectCls} value={form.sexo} onChange={set('sexo')}>
                  {SEXO_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </Section>

          {/* ── Datos de contacto ───────────────────────────────────────────── */}
          <Section icon={<Phone size={14} />} title="Datos de contacto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Correo electrónico</label>
                <input className={inputCls} value={profile?.email ?? ''} disabled />
                <p className="text-xs text-gray-400 mt-1">El correo no se puede modificar directamente.</p>
              </div>
              <div>
                <label className={labelCls}>Teléfono</label>
                <input className={inputCls} value={form.telefono} onChange={set('telefono')} placeholder="10 dígitos" maxLength={20} />
              </div>
            </div>
          </Section>

          {/* ── Especialidades (solo médicos) ───────────────────────────────── */}
          {esMedicoRol && profile?.id && (
            <Section icon={<Stethoscope size={14} />} title="Especialidades y cédulas">
              <EspecialidadesManager usuarioId={profile.id} />
              <p className="mt-3 text-xs text-gray-400">
                Para editar tu bio, subespecialidades y perfil público ve a{' '}
                <a href="/red/mi-perfil" className="text-primary-600 hover:underline">Red médica → Mi perfil</a>.
              </p>
            </Section>
          )}

          {/* ── Logo en plantillas (solo médicos) ──────────────────────────── */}
          {esMedicoRol && (
            <Section icon={<ImageIcon size={14} />} title="Logo en plantillas">
              <p className="text-xs text-text-secondary mb-4">
                Elige qué logo aparecerá en tus recetas, órdenes, presupuestos y otros documentos.
              </p>

              {/* Opción: logo de la clínica */}
              <label className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors mb-2"
                style={{ borderColor: !usandoPropio ? '#0e85bf' : undefined, backgroundColor: !usandoPropio ? '#f0f9ff' : undefined }}>
                <input
                  type="radio"
                  name="tipoLogo"
                  checked={!usandoPropio}
                  onChange={handleLogoDelete}
                  className="accent-primary-600"
                />
                <div className="w-12 h-12 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {clinicaLogoUrl
                    ? <img src={clinicaLogoUrl} alt="Logo clínica" className="w-full h-full object-contain p-0.5" />
                    : <Building2 size={20} className="text-gray-300" />
                  }
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Logo de la clínica</p>
                  <p className="text-xs text-gray-400">{clinicaLogoUrl ? 'Logo configurado' : 'Sin logo configurado'}</p>
                </div>
              </label>

              {/* Opción: logo propio */}
              <label className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors"
                style={{ borderColor: usandoPropio ? '#0e85bf' : undefined, backgroundColor: usandoPropio ? '#f0f9ff' : undefined }}>
                <input
                  type="radio"
                  name="tipoLogo"
                  checked={usandoPropio}
                  onChange={() => logoInputRef.current?.click()}
                  className="accent-primary-600"
                />
                <div className="w-12 h-12 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {logoPropio
                    ? <img src={logoPropio} alt="Logo propio" className="w-full h-full object-contain p-0.5" />
                    : <Upload size={20} className="text-gray-300" />
                  }
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">Logo propio</p>
                  <p className="text-xs text-gray-400">{logoPropio ? 'Logo personalizado activo' : 'Sube tu propio logo'}</p>
                </div>
                {usandoPropio && (
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); logoInputRef.current?.click(); }}
                    disabled={uploadingLogo}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    {uploadingLogo
                      ? <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      : <Upload size={12} />
                    }
                    Cambiar
                  </button>
                )}
              </label>

              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleLogoFileChange}
              />
            </Section>
          )}

          {/* ── Acceso ──────────────────────────────────────────────────────── */}
          <Section icon={<Shield size={14} />} title="Acceso">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Rol en el sistema</label>
                <input className={inputCls} value={ROL_LABEL[profile?.rolId] ?? profile?.rolId ?? ''} disabled />
              </div>
              {profile?.clinica && (
                <div>
                  <label className={labelCls}>Clínica</label>
                  <input className={inputCls} value={profile.clinica.nombre ?? ''} disabled />
                </div>
              )}
            </div>
          </Section>

          {/* ── Acción guardar ──────────────────────────────────────────────── */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="flex items-center gap-2 px-5 py-2.5 text-white text-sm font-medium rounded-xl disabled:opacity-60 transition-colors"
              style={{ backgroundColor: '#0e85bf' }}
              onMouseEnter={e => { if (!saveMutation.isPending) e.currentTarget.style.backgroundColor = '#0c76aa'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#0e85bf'; }}
            >
              {saveMutation.isPending
                ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                : <Save size={14} />
              }
              Guardar cambios
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
